import { useRef, useState } from 'react';
import {
  CARGO_PROBLEM_FILE_MAX_BYTES,
  CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX,
  CARGO_PROBLEM_VIDEO_ALLOWED_MIME,
  CARGO_PROBLEM_VIDEO_MAX_BYTES,
  ErrorCode,
  REQUEST_FILE_ALLOWED_MIME,
  type CargoProblemMessageAttachmentInput,
  type CargoProblemVideoMime,
  type RequestFileMime,
} from '@magimp/shared';
import { issueCargoProblemAttachmentUpload } from '../../api/cargo-problems.js';
import { ApiClientError } from '../../api/errors.js';
import { requestUploadUrl } from '../../api/requests/index.js';

export type CargoProblemAttachmentError = 'count' | 'name' | 'size' | 'type' | 'upload';
export type CargoProblemAttachmentMode = 'brief' | 'workflow';

export interface CargoProblemSelectedAttachment {
  id: string;
  file: File;
  uploaded?: CargoProblemMessageAttachmentInput;
  error?: CargoProblemAttachmentError;
}

const ORDINARY_MIME = new Set<string>(REQUEST_FILE_ALLOWED_MIME);
const VIDEO_MIME = new Set<string>(CARGO_PROBLEM_VIDEO_ALLOWED_MIME);

function isOrdinaryMime(value: string): value is RequestFileMime {
  return ORDINARY_MIME.has(value);
}

export function isCargoProblemVideoMime(value: string): value is CargoProblemVideoMime {
  return VIDEO_MIME.has(value);
}

export function validateCargoProblemAttachment(file: File): CargoProblemAttachmentError | null {
  if (!file.name.trim() || file.name.length > 255) return 'name';
  if (isCargoProblemVideoMime(file.type)) {
    return file.size > CARGO_PROBLEM_VIDEO_MAX_BYTES ? 'size' : null;
  }
  if (!isOrdinaryMime(file.type)) return 'type';
  return file.size > CARGO_PROBLEM_FILE_MAX_BYTES ? 'size' : null;
}

async function issueUpload(problemId: string, file: File, mode: CargoProblemAttachmentMode) {
  if (isCargoProblemVideoMime(file.type)) {
    return issueCargoProblemAttachmentUpload(problemId, {
      scope: 'cargo-problem-video',
      contentType: file.type,
      contentLength: file.size,
    });
  }
  if (!isOrdinaryMime(file.type)) throw new Error('unsupported cargo-problem attachment MIME');
  if (mode === 'brief') {
    return issueCargoProblemAttachmentUpload(problemId, {
      contentType: file.type,
      contentLength: file.size,
    });
  }
  return requestUploadUrl({
    scope: 'cargo-problem-attachment',
    contentType: file.type,
    contentLength: file.size,
  });
}

export async function uploadCargoProblemAttachment(
  problemId: string,
  file: File,
  mode: CargoProblemAttachmentMode,
): Promise<CargoProblemMessageAttachmentInput> {
  const signed = await issueUpload(problemId, file, mode);
  let response: Response;
  try {
    response = await fetch(signed.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'content-type': file.type },
    });
  } catch {
    throw new Error('cargo-problem attachment upload failed');
  }
  if (!response.ok) throw new Error('cargo-problem attachment upload failed');
  return {
    objectKey: signed.objectKey,
    fileName: file.name,
    contentType: file.type,
    contentLength: file.size,
  } as CargoProblemMessageAttachmentInput;
}

export function cargoProblemExpiredObjectKey(error: unknown): string | null {
  if (!(error instanceof ApiClientError) || error.code !== ErrorCode.CARGO_PROBLEM_UPLOAD_EXPIRED) {
    return null;
  }
  const key = error.meta?.['expiredObjectKey'];
  return typeof key === 'string' ? key : null;
}

export function useCargoProblemAttachments() {
  const nextId = useRef(0);
  const busyRef = useRef(false);
  const [items, setItems] = useState<CargoProblemSelectedAttachment[]>([]);
  const [busy, setBusy] = useState(false);

  function add(files: readonly File[]): CargoProblemAttachmentError | null {
    if (busyRef.current) return null;
    if (items.length + files.length > CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX) return 'count';
    for (const file of files) {
      const error = validateCargoProblemAttachment(file);
      if (error) return error;
    }
    const additions = files.map((file) => ({
      id: `${file.name}-${file.lastModified}-${nextId.current++}`,
      file,
    }));
    setItems((current) => [...current, ...additions]);
    return null;
  }

  function remove(id: string) {
    if (busyRef.current) return;
    setItems((current) => current.filter((item) => item.id !== id));
  }

  async function uploadPending(
    problemId: string,
    mode: CargoProblemAttachmentMode,
    source: CargoProblemSelectedAttachment[],
  ): Promise<CargoProblemSelectedAttachment[]> {
    let current = source;
    for (const item of current) {
      if (item.uploaded) continue;
      try {
        const uploaded = await uploadCargoProblemAttachment(problemId, item.file, mode);
        current = current.map((candidate) => {
          if (candidate.id !== item.id) return candidate;
          const { error: _error, ...rest } = candidate;
          return { ...rest, uploaded };
        });
        setItems(current);
      } catch (error) {
        current = current.map((candidate) => {
          if (candidate.id !== item.id) return candidate;
          const { uploaded: _uploaded, ...rest } = candidate;
          return { ...rest, error: 'upload' };
        });
        setItems(current);
        throw error;
      }
    }
    return current;
  }

  async function commitWithRecovery<T>(
    problemId: string,
    mode: CargoProblemAttachmentMode,
    commit: (attachments: CargoProblemMessageAttachmentInput[]) => Promise<T>,
  ): Promise<T> {
    busyRef.current = true;
    setBusy(true);
    try {
      let current = await uploadPending(problemId, mode, items);
      const ready = () => current.flatMap((item) => (item.uploaded ? [item.uploaded] : []));
      try {
        return await commit(ready());
      } catch (error) {
        const expiredKey = cargoProblemExpiredObjectKey(error);
        const expired = current.find((item) => item.uploaded?.objectKey === expiredKey);
        if (!expiredKey || !expired || !isCargoProblemVideoMime(expired.file.type)) throw error;
        try {
          const uploaded = await uploadCargoProblemAttachment(problemId, expired.file, mode);
          current = current.map((item) => {
            if (item.id !== expired.id) return item;
            const { error: _error, ...rest } = item;
            return { ...rest, uploaded };
          });
          setItems(current);
        } catch (uploadError) {
          current = current.map((item) => {
            if (item.id !== expired.id) return item;
            const { uploaded: _uploaded, ...rest } = item;
            return { ...rest, error: 'upload' };
          });
          setItems(current);
          throw uploadError;
        }
        return commit(ready());
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  function clear() {
    setItems([]);
  }

  return { add, busy, clear, commitWithRecovery, items, remove };
}
